import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoTexto } from '@/componentes/Campo';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { useSessao } from '@/modulos/sessao/consultas';
import { formatarDataHora, formatarInteiro, interpretarDataHora, interpretarNumero, paraCampoDataHora } from '@/lib/formato';

import type { TransferenciaDaApi } from './api';
import { explicarFalha, useRegistrarChegada, useTransferencias } from './consultas';

/**
 * AS TRANSFERÊNCIAS ENTRE TANQUES, e o que está na estrada (0062).
 *
 * A transferência tem duas etapas: sai de um tanque no caminhão, e chega em
 * outro — às vezes em outra sede, horas depois. Entre as duas, o combustível
 * não está em tanque nenhum, e esta lista é onde ele aparece. "Registrar
 * chegada" diz quanto entrou; se entrou menos do que saiu, a diferença é a
 * perda no transporte, com o porquê escrito, e fica no histórico dos dois
 * tanques.
 *
 * Registrar a chegada não é laranja: a ação laranja da tela é a do hub. A
 * janela de confirmação é que tem a ação principal.
 */
export function TransferenciasEmTransito() {
  const consulta = useTransferencias();
  const chegada = useRegistrarChegada();
  const base = useId();
  /* Registrar a chegada é de quem recebe no tanque (`tanque.receber`); quem só
     lê vê a transferência na estrada, sem o botão (prova do marco, 07/10/2026). */
  const sessao = useSessao();
  const podeReceber = (sessao.data?.permissoes ?? []).includes('tanque.receber');
  const [alvo, setAlvo] = useState<TransferenciaDaApi | null>(null);
  const [litros, setLitros] = useState('');
  const [quando, setQuando] = useState('');
  const [justificativa, setJustificativa] = useState('');

  const transferencias = consulta.data ?? [];
  const paginaDasTransferencias = usePaginacao(transferencias);
  const emTransito = transferencias.filter((t) => t.situacao === 'em_transito');

  function abrir(t: TransferenciaDaApi) {
    setAlvo(t);
    setLitros(formatarInteiro(t.quantidadeSaidaL).replace(/\./g, ''));
    setQuando(paraCampoDataHora(new Date()));
    setJustificativa('');
    chegada.reset();
  }
  function fechar() {
    setAlvo(null);
    chegada.reset();
  }

  const chegou = interpretarNumero(litros);
  const instante = interpretarDataHora(quando);
  const perda = alvo !== null && chegou !== null ? alvo.quantidadeSaidaL - chegou : null;
  const temPerda = perda !== null && perda > 0;
  const erroDosLitros =
    alvo === null || litros.trim() === ''
      ? null
      : chegou === null || chegou <= 0
        ? 'Informe quantos litros entraram no tanque.'
        : chegou > alvo.quantidadeSaidaL
          ? `Saíram ${formatarInteiro(alvo.quantidadeSaidaL)}\u00a0L. Não pode chegar mais do que saiu; confira a medição.`
          : null;
  const podeGravar =
    alvo !== null && chegou !== null && chegou > 0 && erroDosLitros === null && instante !== null
    && (!temPerda || justificativa.trim().length >= 10);

  const COLUNAS: readonly Coluna<TransferenciaDaApi>[] = [
    {
      chave: 'trajeto',
      rotulo: 'De onde para onde',
      prioridade: 'chave',
      celula: (t) => (
        <>
          <TextoDaCelula texto={`${t.origemCodigo} → ${t.destinoCodigo}`} classe="codigo text-tinta" linhas={1} />
          <TextoDaCelula
            texto={`${t.origemFilial ?? 'sem filial'} → ${t.destinoFilial ?? 'sem filial'}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </>
      ),
    },
    {
      chave: 'caminhao',
      rotulo: 'Caminhão',
      celula: (t) => (
        <TextoDaCelula
          texto={t.caminhaoCodigo ? `${t.caminhaoCodigo} · ${t.caminhaoDescricao ?? ''}` : 'Sem caminhão'}
          classe="txt-corpo text-tinta"
          linhas={1}
        />
      ),
    },
    {
      chave: 'litros',
      rotulo: 'Litros',
      alinhamento: 'numero',
      celula: (t) => (
        <>
          <span className="numero text-tinta">{`${formatarInteiro(t.quantidadeSaidaL)}\u00a0L saíram`}</span>
          {t.quantidadeChegadaL === null ? null : (
            <span className="txt-linha2 block text-tinta-secundaria">
              {`${formatarInteiro(t.quantidadeChegadaL)}\u00a0L chegaram`}
            </span>
          )}
        </>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      /* Rótulo escrito (p.09). A perda vai por extenso, com o porquê. */
      celula: (t) => (
        <div className="min-w-0">
          <span className="txt-corpo text-tinta">{t.situacao === 'em_transito' ? 'Em trânsito' : 'Chegou'}</span>
          <TextoDaCelula
            texto={
              t.situacao === 'em_transito'
                ? `Saiu em ${formatarDataHora(new Date(t.saiuEm))}${t.saidaPor ? ` · ${t.saidaPor}` : ''}`
                : `${t.chegouEm ? formatarDataHora(new Date(t.chegouEm)) : ''}${t.chegadaPor ? ` · ${t.chegadaPor}` : ''}${
                    t.perdaL && t.perdaL > 0 ? ` · perda de ${formatarInteiro(t.perdaL)}\u00a0L: ${t.justificativaDaPerda ?? ''}` : ''
                  }`
            }
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="txt-secao text-tinta">Transferências entre tanques</h2>
        {/* "Nenhum combustível em trânsito" antes da resposta afirmava o que
            ninguém sabia (p.32): a linha é esqueleto, e a tabela anuncia. */}
        {consulta.isPending ? (
          <Barra className="my-1 h-3 w-1/2" />
        ) : (
          <p className="text-tinta-secundaria">
            {emTransito.length === 0
              ? 'Nenhum combustível em trânsito agora.'
              : `${formatarInteiro(emTransito.reduce((s, t) => s + t.quantidadeSaidaL, 0))}\u00a0L em trânsito, em ${
                  emTransito.length === 1 ? 'uma transferência' : `${formatarInteiro(emTransito.length)} transferências`
                }. Quem recebe registra a chegada.`}
          </p>
        )}
      </div>

      <Tabela
        rotulo="Transferências entre tanques"
        colunas={COLUNAS}
        linhas={paginaDasTransferencias.visiveis}
        rodape={<RodapeTabela {...paginaDasTransferencias.rodape} />}
        alturaDupla
        chaveDaLinha={(t) => t.id}
        carregando={consulta.isPending}
        acoes={(t) =>
          podeReceber && t.situacao === 'em_transito' ? (
            <Botao nivel="terciario" onClick={() => { abrir(t); }}>Registrar chegada</Botao>
          ) : null
        }
        erro={
          consulta.isError ? (
            <EstadoVazio
              titulo="Não foi possível carregar as transferências"
              descricao="A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário em que aconteceu."
              acao={
                <Botao nivel="secundario" onClick={() => { void consulta.refetch(); }}>
                  Carregar as transferências
                </Botao>
              }
            />
          ) : null
        }
        vazio={
          <EstadoVazio
            titulo="Nenhuma transferência ainda"
            descricao="Transfira pelos três pontos da linha do tanque de origem. O combustível fica em trânsito até quem recebe registrar a chegada."
          />
        }
      />

      <Janela
        titulo="Registrar chegada"
        aberta={alvo !== null && podeReceber}
        aoFechar={fechar}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
            <Botao
              nivel="primario"
              disabled={!podeGravar || chegada.isPending}
              onClick={() => {
                if (alvo === null || chegou === null || instante === null) return;
                chegada.mutate(
                  {
                    id: alvo.id,
                    contexto: {
                      alvo: { codigo: alvo.destinoCodigo, nome: alvo.destinoNome },
                      fatos: [`de ${alvo.origemCodigo} · ${alvo.origemNome}`],
                    },
                    envio: {
                      quantidadeL: chegou,
                      chegouEm: instante.toISOString(),
                      ...(temPerda ? { justificativa: justificativa.trim() } : {}),
                    },
                  },
                  { onSuccess: fechar },
                );
              }}
            >
              {chegada.isPending ? 'Registrando…' : 'Registrar chegada'}
            </Botao>
          </>
        }
      >
        {alvo === null ? null : (
          <div className="flex flex-col gap-4">
            {/* A recusa no TOPO do formulário (p.22). */}
            {chegada.isError ? (
              <p role="alert" className="txt-apoio text-parada">
                {`${explicarFalha(chegada.error).erro}. ${explicarFalha(chegada.error).comoResolver}`}
              </p>
            ) : null}
            <p>
              {`Saíram ${formatarInteiro(alvo.quantidadeSaidaL)}\u00a0L de ${alvo.origemNome} em ${formatarDataHora(new Date(alvo.saiuEm))}`}
              {alvo.caminhaoCodigo ? `, no caminhão ${alvo.caminhaoCodigo}` : ''}
              {`, para ${alvo.destinoNome}. Informe quanto entrou no tanque.`}
            </p>
            <Campo id={`${base}-litros`} rotulo="Litros que chegaram" erro={erroDosLitros}>
              <div className="flex items-center gap-2">
                <input
                  id={`${base}-litros`}
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={litros}
                  className="controle w-full"
                  onChange={(e) => { setLitros(e.target.value); }}
                />
                <span className="txt-corpo shrink-0 text-tinta-apagada">L</span>
              </div>
            </Campo>
            <Campo id={`${base}-quando`} rotulo="Data e hora da chegada" erro={instante === null && quando !== '' ? 'Informe a data e a hora da chegada.' : null}>
              <input
                id={`${base}-quando`}
                type="datetime-local"
                value={quando}
                className="controle w-full"
                onChange={(e) => { setQuando(e.target.value); }}
              />
            </Campo>
            {temPerda && perda !== null ? (
              <CampoTexto
                rotulo={`Por que chegaram ${formatarInteiro(perda)}\u00a0L a menos`}
                valor={justificativa}
                aoMudar={setJustificativa}
                maxLength={300}
                placeholder="Evaporação e sobra na mangueira do caminhão"
                ajuda="A perda fica no histórico dos dois tanques, com o seu nome e esta frase."
                validar={(v) => (v.trim().length < 10 ? 'Escreva o motivo da perda com pelo menos 10 caracteres.' : null)}
              />
            ) : null}
          </div>
        )}
      </Janela>
    </section>
  );
}
